import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";
import OAuthCallback from "./pages/OAuthCallback";
import InviteAccept from "./pages/InviteAccept";
import NotAuthorized from "./pages/NotAuthorized";
import MainLayout from "./components/MainLayout";
import Items from "./pages/Items";
import ScanStation from "./pages/ScanStation";
import OrderQueue from "./pages/OrderQueue";
import Receiving from "./pages/Receiving";
import AdminUsers from "./pages/admin/Users";
import AdminInvitations from "./pages/admin/Invitations";
import AdminNotifications from "./pages/admin/Notifications";
import AdminGoogleWizard from "./pages/admin/GoogleWizard";
import AdminSalesforce from "./pages/admin/SalesforcePanel";
import AdminSettings from "./pages/admin/Settings";
import AdminMasterData from "./pages/admin/MasterData";
import "./App.css";

/**
 * AuthGuard - Protects routes from unauthenticated access
 * 
 * Key behaviors:
 * - Shows loading spinner while auth is being checked
 * - Redirects to /login if not authenticated
 * - Stores intended destination in location.state.next for redirect after login
 * - Checks roles array (user.roles) for authorization
 * - Treats "owner" as having all permissions (including admin)
 * - Shows NotAuthorized page if authenticated but lacks required role
 * - NEVER renders children if not authenticated or not authorized
 */
const AuthGuard = ({ children, allowedRoles }) => {
  const { user, loading, authChecked, isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Still checking auth state
  if (loading || !authChecked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-2">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <div className="text-muted-foreground text-sm">Loading...</div>
        </div>
      </div>
    );
  }
  
  // Not authenticated - redirect to login with "next" state
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ next: location.pathname }} replace />;
  }
  
  // Check role-based access if roles are specified
  if (allowedRoles && allowedRoles.length > 0) {
    // Get user roles as array
    const userRoles = Array.isArray(user?.roles) ? user.roles : [];
    
    // Debug logging (remove after fix)
    console.log('[AuthGuard] Role check:', {
      path: location.pathname,
      allowedRoles,
      userRoles,
      user: user?.email
    });
    
    // Owner has access to everything
    const isOwner = userRoles.includes('owner');
    
    // Check if user has any of the allowed roles
    const hasRequiredRole = allowedRoles.some(role => userRoles.includes(role));
    
    // If user is not owner and doesn't have required role, show NotAuthorized
    if (!isOwner && !hasRequiredRole) {
      console.log('[AuthGuard] Access denied - showing NotAuthorized');
      return <NotAuthorized />;
    }
    
    console.log('[AuthGuard] Access granted');
  }
  
  return children;
};

/**
 * LoginRoute - Shows login page only when NOT authenticated
 * Redirects to intended destination after auth
 */
const LoginRoute = () => {
  const { isAuthenticated, loading, authChecked } = useAuth();
  const location = useLocation();
  
  // Still checking auth
  if (loading || !authChecked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  // Already authenticated - redirect to intended page or default
  if (isAuthenticated) {
    const next = location.state?.next || "/items";
    return <Navigate to={next} replace />;
  }
  
  return <Login />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginRoute />} />
      
      {/* Admin Dashboard (protected by cookie session, not AuthContext) */}
      <Route path="/admin" element={<AdminDashboard />} />
      
      {/* Invite acceptance - public route for accepting invitations */}
      <Route path="/invite" element={<InviteAccept />} />
      
      {/* OAuth callback - handles first-party Google OAuth */}
      <Route path="/auth/callback" element={<OAuthCallback />} />
      <Route path="/auth/relay" element={<OAuthCallback />} />
      
      {/* Protected app routes - ALL behind AuthGuard */}
      <Route path="/" element={
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      }>
        <Route index element={<Navigate to="/items" replace />} />
        <Route path="items" element={<Items />} />
        <Route path="scan" element={
          <AuthGuard allowedRoles={["admin", "owner", "scanner", "viewer"]}>
            <ScanStation />
          </AuthGuard>
        } />
        <Route path="orders" element={
          <AuthGuard allowedRoles={["admin", "owner", "purchasing", "viewer"]}>
            <OrderQueue />
          </AuthGuard>
        } />
        <Route path="receiving" element={
          <AuthGuard allowedRoles={["admin", "owner", "receiving", "viewer"]}>
            <Receiving />
          </AuthGuard>
        } />
        <Route path="users" element={
          <AuthGuard allowedRoles={["admin", "owner"]}>
            <AdminUsers />
          </AuthGuard>
        } />
        <Route path="settings" element={
          <AuthGuard allowedRoles={["admin", "owner"]}>
            <AdminSettings />
          </AuthGuard>
        } />
        <Route path="admin/users" element={
          <AuthGuard allowedRoles={["admin", "owner"]}>
            <AdminUsers />
          </AuthGuard>
        } />
        <Route path="admin/invitations" element={
          <AuthGuard allowedRoles={["admin", "owner"]}>
            <AdminInvitations />
          </AuthGuard>
        } />
        <Route path="admin/notifications" element={
          <AuthGuard allowedRoles={["admin", "owner"]}>
            <AdminNotifications />
          </AuthGuard>
        } />
        <Route path="admin/google" element={
          <AuthGuard allowedRoles={["admin", "owner"]}>
            <AdminGoogleWizard />
          </AuthGuard>
        } />
        <Route path="admin/salesforce" element={
          <AuthGuard allowedRoles={["admin", "owner"]}>
            <AdminSalesforce />
          </AuthGuard>
        } />
        <Route path="admin/settings" element={
          <AuthGuard allowedRoles={["admin", "owner"]}>
            <AdminSettings />
          </AuthGuard>
        } />
        <Route path="admin/master-data" element={
          <AuthGuard allowedRoles={["admin", "owner"]}>
            <AdminMasterData />
          </AuthGuard>
        } />
        
        {/* Catch-all for unknown routes within app - moved inside nested routes */}
        <Route path="*" element={<Navigate to="/items" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
